import React, { useState } from 'react';
import { Menu, LogOut, Loader, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import GlobalSearch from '../admin/GlobalSearch';
import { EnableNotificationsButton } from '../../features/notifications';
import { AdminNotificationBell } from '../admin/notifications/AdminNotificationBell';
import { StoreNotificationBell } from '../store/notifications/StoreNotificationBell';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { getDeviceId } from '../../api/token';
import { resetStoreForm } from '../../redux/storeFormSlice';
import { useContactInfoByAdmin } from '../../hooks/admin/contactInfo.admin';
import { adaptLogoutAll } from '../../redux/authAdapter';
import api from '../../api/apiClient';
import { formatPhoneNumber } from '../../utils/formatNumber';

interface NavbarProps {
  toggleSidebar: () => void;
  title: string;
  headline: string;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar, title, headline }) => {
  const WhatsappIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="#25D366"
      viewBox="0 0 24 24"
    >
      <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 0 5.37 0 12a11.96 11.96 0 001.75 6.26L0 24l5.82-1.54A11.93 11.93 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.26-6.21-3.48-8.52zM12 22c-2.2 0-4.24-.72-5.88-1.92l-.42-.26-3.45.91.92-3.36-.27-.43A9.96 9.96 0 012 12c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10zm5.36-7.72c-.3-.15-1.78-.88-2.06-.98-.28-.1-.48-.15-.68.15s-.78.98-.96 1.18-.35.22-.65.08c-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.17.2-.28.3-.46.1-.17.05-.32-.02-.46-.07-.15-.68-1.64-.93-2.25-.24-.58-.49-.5-.68-.51-.18 0-.39-.02-.6-.02s-.46.07-.7.34c-.24.28-.91.89-.91 2.18s.93 2.53 1.06 2.7c.12.17 1.83 2.8 4.44 3.92.62.27 1.1.43 1.48.55.62.19 1.19.16 1.64.1.5-.07 1.78-.73 2.03-1.44.25-.72.25-1.34.18-1.45-.07-.11-.26-.17-.56-.32z" />
    </svg>
  );

  const { accessToken: ownerToken } = useSelector((state: RootState) => state.owner);
  const { accessToken: storeToken } = useSelector((state: RootState) => state.store);
  const { accessToken: authSliceToken, role } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data } = useContactInfoByAdmin();
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const shouldShowNotifications = role === "STORE" || role === "CUSTOMER";

  async function handleLogout() {
    if (authSliceToken || ownerToken || storeToken) {
      setLoading(true);
      try {
        await api.post("/user-token/logout-current-device", {
          deviceId: getDeviceId()
        });
      } catch (e) {
      } finally {
        setLoading(false);
      }
    }

    dispatch(adaptLogoutAll() as any);
    dispatch(resetStoreForm());
  }

  return (
    <header className="sticky top-0 z-40 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 shadow-sm">
      {/* Main navbar */}
      <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 gap-4">
        {/* Left section: Menu + Title */}
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          <button
            className="block lg:hidden flex-shrink-0 text-slate-700 hover:bg-white/60 hover:text-primary rounded-lg p-2 transition-all duration-200"
            onClick={toggleSidebar}
          >
            <Menu size={24} />
          </button>

          <div className="min-w-0 border-l-2 border-primary/20 pl-3 sm:pl-4">
            {title && (
              <h3 className="text-slate-900 text-lg sm:text-xl font-bold tracking-wide capitalize truncate">
                {title}
              </h3>
            )}
            {headline && (
              <p className="text-slate-500 font-medium text-xs sm:text-sm truncate">{headline}</p>
            )}
          </div>
        </div>
        <div className="hidden md:flex items-center gap-1 lg:gap-3 flex-shrink-0">
          {role === "ADMIN" && (
            <div className="px-2">
              <GlobalSearch />
            </div>
          )}

          {role === "ADMIN" && <AdminNotificationBell />}
          {role === "STORE" && <StoreNotificationBell />}

          <div className="p-2 text-slate-600 hover:bg-white/60 rounded-lg transition-all duration-200">
            <LanguageSwitcher />
          </div>

          {shouldShowNotifications && (
            <div className="p-2 text-slate-600 hover:bg-white/60 rounded-lg transition-all duration-200">
              <EnableNotificationsButton />
            </div>
          )}

          {(storeToken || ownerToken) && (
            <a
              href={`https://wa.me/${formatPhoneNumber(data?.storePhoneNumber ?? "").replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-slate-600 hover:bg-white/60 rounded-lg transition-all duration-200 flex-shrink-0"
            >
              <WhatsappIcon />
            </a>
          )}

          {/* Divider */}
          <div className="hidden lg:block h-6 w-px bg-slate-300 mx-2"></div>

          <button
            onClick={handleLogout}
            disabled={loading}
            className={`bg-primaryButton text-white px-4 py-2.5 rounded-lg hover:shadow-lg transition-all duration-200 flex-shrink-0 flex items-center gap-2 font-medium hover:opacity-90 ${loading ? "opacity-75 cursor-not-allowed" : "active:scale-95"
              }`}
          >
            {loading ? <Loader size={18} className="animate-spin" /> : <LogOut size={18} />}
            <span className="hidden lg:inline">{t("logout", { defaultValue: "Logout" })}</span>
          </button>
        </div>

        {/* Mobile menu button - shown on tablet and smaller */}
        <button
          className="md:hidden flex-shrink-0 text-slate-700 hover:bg-white/60 hover:text-primary rounded-lg p-2 transition-all duration-200"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu - shown on tablet and smaller */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white/80 backdrop-blur-md px-4 py-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          {role === "ADMIN" && (
            <div className="mb-4 pb-4 border-b border-slate-200">
              <GlobalSearch />
            </div>
          )}

          {role === "ADMIN" && (
            <div className="flex items-center gap-3 py-2 hover:bg-slate-50 px-2 rounded-lg transition">
              <AdminNotificationBell />
            </div>
          )}
          {role === "STORE" && (
            <div className="flex items-center gap-3 py-2 hover:bg-slate-50 px-2 rounded-lg transition">
              <StoreNotificationBell />
            </div>
          )}

          <div className="flex items-center gap-3 py-2 px-2 hover:bg-slate-50 rounded-lg transition">
            <span className="text-sm font-medium text-slate-700">Language:</span>
            <LanguageSwitcher />
          </div>

          {shouldShowNotifications && (
            <div className="flex items-center gap-3 py-2 px-2 hover:bg-slate-50 rounded-lg transition">
              <EnableNotificationsButton />
            </div>
          )}

          {(storeToken || ownerToken) && (
            <a
              href={`https://wa.me/${formatPhoneNumber(data?.storePhoneNumber ?? "").replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-2 px-2 hover:bg-slate-50 rounded-lg transition"
            >
              <WhatsappIcon />
              <span className="text-sm font-medium text-slate-700">WhatsApp</span>
            </a>
          )}

          <div className="border-t border-slate-200 pt-3">
            <button
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              disabled={loading}
              className={`w-full bg-primaryButton text-white px-4 py-2.5 rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium hover:opacity-90 ${loading ? "opacity-75 cursor-not-allowed" : "active:scale-95"
                }`}
            >
              {loading ? <Loader size={18} className="animate-spin" /> : <LogOut size={18} />}
              {t("logout", { defaultValue: "Logout" })}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
